/**
 * PythonCodeValidator - Helper for validating Python code syntax
 *
 * This helper provides Python code validation and syntax error detection
 * using Python's built-in ast module for accurate syntax checking.
 */

import { runPythonIsolated } from '../helpers/python-isolated.helper';

export interface ValidationResult {
  isValid: boolean;
  error?: string;
  errorType?: string;
  lineNumber?: number;
  columnNumber?: number;
  suggestions?: string[];
}

export interface ValidationConfig {
  enableSuggestions?: boolean;
  enableLogging?: boolean;
  strictMode?: boolean;
}

export class PythonCodeValidator {
  private readonly python: any;
  private config: ValidationConfig;

  constructor(python: any, config: ValidationConfig = {}) {
    this.python = python;
    this.config = {
      enableSuggestions: true,
      enableLogging: false,
      strictMode: false,
      ...config
    };
  }

  /**
   * Validate Python syntax using AST
   */
  async validateSyntax(content: string): Promise<ValidationResult> {
    if (!content || content.trim() === '') {
      return { isValid: true };
    }

    try {
      const validationCode = `
import ast
import sys
import json

def validate_python_syntax(code):
    result = {
        'is_valid': False,
        'error': None,
        'error_type': None,
        'line_number': None,
        'column_number': None
    }

    try:
        # Try to parse the code with AST
        ast.parse(code)
        result['is_valid'] = True

    except SyntaxError as e:
        result['error'] = str(e.msg) if e.msg else str(e)
        result['error_type'] = 'SyntaxError'
        result['line_number'] = e.lineno
        result['column_number'] = e.offset

    except Exception as e:
        result['error'] = str(e)
        result['error_type'] = type(e).__name__

    return result

# Validate the provided code
code_to_validate = ${JSON.stringify(content)}
validation_result = validate_python_syntax(code_to_validate)
json.dumps(validation_result)
      `;

      // Isolated: this runs before every step, and step code must not see
      // the validator's names or its copy of the source (EHQ-117).
      const resultJson = runPythonIsolated(this.python, validationCode);
      const result = JSON.parse(resultJson);

      if (!result.is_valid && this.config.enableSuggestions) {
        result.suggestions = this.getSyntaxErrorSuggestions(result.error, result.error_type);
      }

      return {
        isValid: result.is_valid,
        error: result.error,
        errorType: result.error_type,
        lineNumber: result.line_number,
        columnNumber: result.column_number,
        suggestions: result.suggestions
      };

    } catch (error: any) {
      // Extract actual error type from exception message (e.g., "IndentationError: ...")
      const errorTypeMatch = error.message?.match(/^(\w+Error):/);
      const errorType = errorTypeMatch ? errorTypeMatch[1] : 'ValidationError';

      const result: ValidationResult = {
        isValid: false,
        error: `Validation process failed: ${error.message}`,
        errorType: errorType
      };

      if (this.config.enableSuggestions) {
        result.suggestions = ['Check if Python runtime is properly initialized'];
      }

      return result;
    }
  }

  /**
   * Get suggestions for common syntax errors
   */
  getSyntaxErrorSuggestions(error: string, _errorType: string): string[] {
    const suggestions: string[] = [];
    const errorLower = error?.toLowerCase() || '';

    // Common syntax error patterns and suggestions
    if (errorLower.includes('invalid syntax')) {
      suggestions.push('Check for missing colons (:) after if, for, while, def, or class statements');
      suggestions.push('Verify that all parentheses, brackets, and braces are properly closed');
      suggestions.push('Make sure indentation is consistent (use either spaces or tabs, not both)');
    }

    if (errorLower.includes('unexpected eof')) {
      suggestions.push('Check for unclosed parentheses, brackets, or quotes');
      suggestions.push('Make sure all code blocks are properly completed');
    }

    if (errorLower.includes('indentation')) {
      suggestions.push('Python uses indentation to define code blocks - ensure consistent spacing');
      suggestions.push('Use 4 spaces for each indentation level (recommended)');
      suggestions.push('Avoid mixing tabs and spaces for indentation');
    }

    if (errorLower.includes('unmatched')) {
      suggestions.push('Check for unmatched parentheses, brackets, or braces');
      suggestions.push('Verify that all opening brackets have corresponding closing brackets');
    }

    if (errorLower.includes('unterminated string')) {
      suggestions.push('Check for unclosed quotes in string literals');
      suggestions.push('Make sure multiline strings use triple quotes (""" or \'\'\')');
    }

    if (errorLower.includes('invalid character')) {
      suggestions.push('Check for special characters that are not valid in Python');
      suggestions.push('Remove any non-ASCII characters unless they are in strings or comments');
    }

    if (errorLower.includes('keyword')) {
      suggestions.push('Avoid using Python keywords as variable names');
      suggestions.push('Common keywords: if, else, for, while, def, class, import, return, etc.');
    }

    if (errorLower.includes('expression')) {
      suggestions.push('Check that expressions are complete and properly formed');
      suggestions.push('Verify operator usage and operand placement');
    }

    // If no specific suggestions were found, provide general ones
    if (suggestions.length === 0) {
      suggestions.push('Review the code syntax around the error location');
      suggestions.push('Check for common Python syntax requirements');
      suggestions.push('Verify proper indentation and punctuation');
    }

    return suggestions;
  }

  /**
   * Validate specific Python constructs
   */
  async validateConstruct(content: string, constructType: 'function' | 'class' | 'import' | 'expression'): Promise<ValidationResult> {
    const wrappedContent = this.wrapContentForValidation(content, constructType);
    return this.validateSyntax(wrappedContent);
  }

  /**
   * Wrap content for specific construct validation
   */
  private wrapContentForValidation(content: string, constructType: string): string {
    switch (constructType) {
      case 'function':
        return `def temp_function():\n    ${content.replace(/\n/g, '\n    ')}`;
      case 'class':
        return `class TempClass:\n    ${content.replace(/\n/g, '\n    ')}`;
      case 'import':
        return content; // imports are validated as-is
      case 'expression':
        return `result = ${content}`; // wrap expression in assignment
      default:
        return content;
    }
  }

  /**
   * Check for potentially dangerous code patterns
   */
  async checkForDangerousPatterns(content: string): Promise<{
    hasDangerousPatterns: boolean;
    patterns: Array<{ pattern: string, line: number, severity: 'low' | 'medium' | 'high' }>
  }> {
    const dangerousPatterns = [
      { pattern: 'eval\\s*\\(', severity: 'high' as const, description: 'Use of eval() function' },
      { pattern: 'exec\\s*\\(', severity: 'high' as const, description: 'Use of exec() function' },
      { pattern: '__import__\\s*\\(', severity: 'medium' as const, description: 'Dynamic import' },
      { pattern: 'os\\.system\\s*\\(', severity: 'high' as const, description: 'System command execution' },
      { pattern: 'subprocess\\.(call|run|Popen)', severity: 'high' as const, description: 'Subprocess execution' },
      { pattern: 'open\\s*\\([^)]*[\'"]w[\'"]', severity: 'medium' as const, description: 'File writing operation' }
    ];

    const foundPatterns: Array<{ pattern: string, line: number, severity: 'low' | 'medium' | 'high' }> = [];
    const lines = content.split('\n');

    lines.forEach((line, index) => {
      dangerousPatterns.forEach(({ pattern, severity, description }) => {
        const regex = new RegExp(pattern, 'i');
        if (regex.test(line)) {
          foundPatterns.push({
            pattern: description,
            line: index + 1,
            severity
          });
        }
      });
    });

    return {
      hasDangerousPatterns: foundPatterns.length > 0,
      patterns: foundPatterns
    };
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<ValidationConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get current configuration
   */
  getConfig(): ValidationConfig {
    return { ...this.config };
  }
}
